import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Shield, Cpu, Activity, Sparkles, Bell, ChevronRight, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HomePage() {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter your email");
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/newsletter/subscribe`, { email });
      if (response.data.success) {
        toast.success(response.data.message);
        setEmail("");
      } else {
        toast.info(response.data.message);
      }
    } catch (error) {
      toast.error("Failed to subscribe. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const features = [
    {
      icon: Shield,
      title: "Medical Devices",
      description: "Next-generation medical devices engineered with precision for patient safety and regulatory excellence.",
      span: "md:col-span-2",
    },
    {
      icon: Cpu,
      title: "Healthcare Apps",
      description: "Intelligent digital health solutions connecting patients with providers.",
      span: "md:col-span-1",
    },
    {
      icon: Activity,
      title: "Patient-Centric",
      description: "Every innovation begins and ends with patient outcomes.",
      span: "md:col-span-1",
    },
    {
      icon: Zap,
      title: "Quality Assurance",
      description: "Rigorous testing protocols ensuring compliance with India's healthcare standards and DPDP regulations.",
      span: "md:col-span-2",
    },
  ];

  return (
    <div className="noise-bg min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-20 overflow-hidden" data-testid="hero-section">
        {/* Background effects */}
        <div className="absolute inset-0 bg-gradient-to-b from-cyan-500/5 via-transparent to-transparent"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-cyan-500/10 rounded-full blur-[120px]"></div>
        
        {/* Grid pattern overlay */}
        <div className="absolute inset-0 opacity-[0.02]" style={{
          backgroundImage: `linear-gradient(rgba(6, 182, 212, 0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(6, 182, 212, 0.3) 1px, transparent 1px)`,
          backgroundSize: '50px 50px'
        }}></div>

        <div className="container-custom relative z-10">
          <div className="max-w-4xl">
            <div className="mb-6 opacity-0 animate-fade-up stagger-1">
              <span className="badge" data-testid="hero-badge">
                <Sparkles className="w-3 h-3 mr-1" />
                Healthcare Innovation
              </span>
            </div>
            
            <h1 className="heading-1 text-white mb-6 opacity-0 animate-fade-up stagger-2" data-testid="hero-title">
              The Future of{" "}
              <span className="gradient-text">Medical Protection</span>
            </h1>
            
            <p className="body-large max-w-2xl mb-10 opacity-0 animate-fade-up stagger-3" data-testid="hero-description">
              BastionGuard Medical Technologies is pioneering advanced healthcare technologies 
              designed to safeguard life with precision, intelligence, and unwavering commitment to excellence.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 mb-12 opacity-0 animate-fade-up stagger-4">
              <Link to="/products">
                <Button className="btn-primary" data-testid="hero-cta-primary">
                  Explore Our Vision
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button className="btn-secondary" data-testid="hero-cta-secondary">
                  Contact Us
                </Button>
              </Link>
            </div>

            {/* Newsletter Signup */}
            <div className="max-w-md opacity-0 animate-fade-up stagger-5" data-testid="hero-newsletter">
              <p className="text-sm text-slate-500 mb-3">Join the future of healthcare</p>
              <form onSubmit={handleNewsletterSubmit} className="flex gap-2">
                <Input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-dark flex-1"
                  data-testid="hero-newsletter-email"
                />
                <Button 
                  type="submit" 
                  className="btn-primary px-6"
                  disabled={isLoading}
                  data-testid="hero-newsletter-submit"
                >
                  Subscribe
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* What We Do - Bento Grid */}
      <section className="section-padding relative" data-testid="what-we-do-section">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="caption mb-4 block" data-testid="what-we-do-label">What We Do</span>
            <h2 className="heading-2 text-white" data-testid="what-we-do-title">
              Comprehensive Healthcare Solutions
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="bento-grid">
            {features.map((feature, index) => (
              <div
                key={feature.title}
                className={`card-glass group ${feature.span}`}
                data-testid={`bento-item-${index}`}
              >
                <div className="icon-glow mb-6 group-hover:glow-cyan transition-all duration-300">
                  <feature.icon className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>{feature.title}</h3>
                <p className="body-base">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Coming Soon Products Preview */}
      <section className="section-padding relative overflow-hidden" data-testid="products-preview-section">
        {/* Background glow */}
        <div className="absolute top-1/2 right-0 translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-cyan-500/10 rounded-full blur-[100px]"></div>
        
        <div className="container-custom relative z-10">
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
            <div className="flex-1">
              <span className="caption mb-4 block" data-testid="products-preview-label">Coming Soon</span>
              <h2 className="heading-2 text-white mb-6" data-testid="products-preview-title">
                Innovation in Progress
              </h2>
              <p className="body-large mb-8" data-testid="products-preview-description">
                Our team of engineers and healthcare professionals are working on groundbreaking medical devices 
                and healthcare applications. Be the first to know when we launch.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/products">
                  <Button className="btn-primary" data-testid="products-preview-cta">
                    <Bell className="w-5 h-5 mr-2" />
                    Get Notified
                  </Button>
                </Link>
                <Link to="/about">
                  <Button className="btn-ghost" data-testid="products-preview-about-cta">
                    Learn About Us
                    <ChevronRight className="w-5 h-5 ml-1" />
                  </Button>
                </Link>
              </div>
            </div>
            <div className="flex-1 w-full">
              <div className="relative">
                {/* Futuristic card */}
                <div className="aspect-square max-w-md mx-auto glass-card rounded-3xl flex items-center justify-center p-8 animate-float">
                  <div className="text-center">
                    <div className="w-24 h-24 mx-auto mb-6 rounded-2xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center animate-glow-pulse">
                      <Shield className="w-12 h-12 text-cyan-400" />
                    </div>
                    <p className="text-2xl font-semibold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Products</p>
                    <p className="text-cyan-400 text-sm tracking-wider">COMING SOON</p>
                  </div>
                </div>
                {/* Decorative elements */}
                <div className="absolute -top-4 -right-4 w-24 h-24 border border-cyan-500/20 rounded-2xl"></div>
                <div className="absolute -bottom-4 -left-4 w-32 h-32 border border-cyan-500/10 rounded-2xl"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding" data-testid="cta-section">
        <div className="container-custom">
          <div className="glass-card rounded-3xl p-8 md:p-16 text-center relative overflow-hidden">
            {/* Background glow */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[400px] h-[200px] bg-cyan-500/20 rounded-full blur-[80px]"></div>
            
            <div className="relative z-10">
              <h2 className="heading-2 text-white mb-6" data-testid="cta-title">
                Ready to Transform Healthcare Together?
              </h2>
              <p className="body-large max-w-2xl mx-auto mb-8" data-testid="cta-description">
                Whether you're a healthcare provider, investor, or potential partner, 
                we'd love to hear from you.
              </p>
              <Link to="/contact">
                <Button className="btn-primary" data-testid="cta-button">
                  Get in Touch
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
